import React, { useEffect } from 'react';
import styles from '@/styles/OverViewComponent.module.scss';
import { useAppContext } from '@/components/context';
import { getCTAS } from '@/utils';
import { PEIHeadLine, OverViewDataType, PEIImage, PEILink } from '@/common';
import { AboutArticlePanel } from '@/components/sections/AboutArticlePanel';
import { ZephrFeature } from '@/components/ZephrFeature';

type OverViewComponentProps = {
  institutionID: string;
  institutionName: string;
  partnerStatus: string;
  overviewData: OverViewDataType;
  userType: string;
  name: string;
  firstAboutArticle?: any;
};

export const OverViewComponent = ({
  institutionName,
  institutionID,
  overviewData,
  partnerStatus,
  userType,
  name,
  firstAboutArticle,
}: OverViewComponentProps) => {
  const { dispatch } = useAppContext();

  useEffect(() => {
    dispatch({
      type: 'setRecordInfo',
      payload: {
        key: 'overview',
        value: overviewData != undefined || JSON.stringify(overviewData) !== '{}' ? true : false,
      },
    });
  }, [dispatch, overviewData]);

  const allData = getCTAS(userType, institutionName, institutionID);
  const overViewComponentData: {
    ctaHeading?: string;
    LP: string;
    GP: string;
    'LP/GP': string;
    requestLink?: undefined | string;
    heading?: undefined | string;
    subHeading?: undefined | string;
    buttonText?: undefined | string;
  } = allData['OverViewComponent'];

  const ctas = {
    ctaButton: '',
  };

  switch (partnerStatus) {
    case 'LP':
      ctas.ctaButton = overViewComponentData.LP;
      break;
    case 'GP':
      ctas.ctaButton = overViewComponentData.GP;
      break;
    case 'LP/GP':
      ctas.ctaButton = overViewComponentData['LP/GP'];
      break;
  }

  return (
    <main className={styles.container} id='overview'>
      <article className={`${styles['overview-container']}${firstAboutArticle ? ` ${styles['overview-container--with-article']}` : ''}`}>
        <PEIHeadLine text={`${institutionName} overview`} />
        <main className={styles['overview-content-container']}>
          <aside>
            <ZephrFeature
              placeholder={<p className={styles.description}>{PLACEHOLDER_DESCRIPTION}</p>}
            >
              <BackgroundText
                background={overviewData.description.background}
                institutionName={institutionName}
                brandName={'Private Equity International'}
              />
            </ZephrFeature>
          </aside>

          <aside className={`${styles['overview-content-details']}`}>
            <div className={styles['aum-container']}>
              <p className={styles['text-aum']}>AUM</p>
              <p className={`${styles['aum-rows']}`}>
                <PEIImage
                  src={'./account.svg'}
                  alt='lock icon image'
                  height={21}
                  width={21}
                  className={styles['overview-logos']}
                />

                <PEILink className={styles.link} href={ctas.ctaButton} target='_blank'>
                  {overViewComponentData.ctaHeading}{' '}
                  <span className={styles['keep-together']}>AUM &gt;</span>
                </PEILink>
              </p>
            </div>
            {overviewData.hQ.address.city && overviewData.hQ.address.city.name != '' && (
              <ZephrFeature
                placeholder={
                  <OverviewRowPlaceholder icon='./marker.svg' text='Headquarters: City, Country' />
                }
              >
                <div className={styles['aum-rows']}>
                  <PEIImage
                    src={'./marker.svg'}
                    alt='location market image'
                    height={21}
                    width={21}
                    className={styles['overview-logos']}
                  />

                  <span className={styles.text}>
                    <span className={styles['text-correction']}>
                      Headquarters: {overviewData.hQ.address.city.name},{' '}
                      {overviewData.hQ.address.country.name}
                    </span>
                  </span>
                </div>
              </ZephrFeature>
            )}
            {overviewData.website && (
              <ZephrFeature
                placeholder={
                  <OverviewRowPlaceholder icon='./basketball.svg' text='Website: www.example.com' />
                }
              >
                <div className={styles['aum-rows']}>
                  <PEIImage
                    src={'./basketball.svg'}
                    alt='basketball image'
                    height={21}
                    width={21}
                    className={styles['overview-logos']}
                  />
                  <span className={styles.text}>
                    <span className={styles['text-correction']}>Website: {overviewData.website}</span>
                  </span>
                </div>
              </ZephrFeature>
            )}
            {overviewData.email && (
              <ZephrFeature
                placeholder={
                  <OverviewRowPlaceholder icon='./envelope.svg' text='Email: contact@example.com' />
                }
              >
                <div className={styles['aum-rows']}>
                  <PEIImage
                    src={'./envelope.svg'}
                    alt='email image'
                    height={21}
                    width={21}
                    className={styles['overview-logos']}
                  />
                  <span className={styles.text}>
                    <span className={styles['text-correction']}>Email: {overviewData.email}</span>
                  </span>
                </div>
              </ZephrFeature>
            )}
          </aside>
        </main>
      </article>
      {firstAboutArticle && <AboutArticlePanel article={firstAboutArticle} />}
    </main>
  );
};

const PLACEHOLDER_DESCRIPTION =
  'This institution is a global alternative asset manager with a multi-decade track ' +
  'record across private markets. The firm invests on behalf of institutional partners ' +
  'through a family of strategies spanning several asset classes and regions. Its teams ' +
  'operate from offices around the world, working with investors and portfolio companies ' +
  'over long holding periods. Subscribe for the full profile, background and analysis.';

const OverviewRowPlaceholder = ({ icon, text }: { icon: string; text: string }) => (
  <div className={styles['aum-rows']}>
    <PEIImage src={icon} alt='' height={21} width={21} className={styles['overview-logos']} />
    <span className={styles.text}>
      <span className={styles['text-correction']}>{text}</span>
    </span>
  </div>
);

type descriptionProps = {
  background: { __html: string } | undefined;
  institutionName: string | undefined;
  brandName: string | undefined;
};

const BackgroundText = ({ background, brandName, institutionName }: descriptionProps) => {
  if (background?.__html === 'No background data exists') {
    return (
      <>
        <p className={styles.description} data-testid='background-welcome-id'>
          Welcome to {brandName}.
        </p>
        <p
          className={styles.description}
          style={{ paddingTop: '3%' }}
          data-testid='background-text-id'
        >
          Access the latest insights, analysis and data for {institutionName} within our extensive
          information platform.
        </p>
      </>
    );
  }
  return <p className={styles.description} dangerouslySetInnerHTML={background}></p>;
};
